import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { debounce } from "lodash";

import {
  getUnits,
  getCategories,
  getSizes,
  fetchItemsByCategoryAndSubcategory,
  getNextBomNo,
  createBOM,
  updateBOM,
  getBOMById,
} from "../../../Services/Services";

// ── Reusable input style ──
const inputSx = {
  "& .MuiInputBase-root": {
    height: 30,
    borderRadius: 0,
    fontSize: "0.75rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.7rem",
    top: -4,
    "&.MuiInputLabel-shrink": { top: 2, fontSize: "0.65rem" },
  },
  "& .MuiOutlinedInput-input": {
    padding: "4px 8px",
    fontSize: "0.75rem",
  },
};

// ── Empty component template ──
const emptyComponent = () => ({
  category: null,
  product_type: null,
  item: null,
  description: "",
  quantity: 1,
  unit: null,
});

export default function BOMCreation() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // ── States ──
  const [units, setUnits] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [componentProductTypeOptions, setComponentProductTypeOptions] =
    useState({});
  const [componentItemOptions, setComponentItemOptions] = useState({});
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    bom_no: "",
    bom_date: new Date().toISOString().split("T")[0],
    bom_name: "",
    components: [emptyComponent()],
  });

  // ── Theme helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  // ══════════════════════════════════════════
  // FETCH ON MOUNT - Units + Sizes only
  // ══════════════════════════════════════════
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [unitsData, categoriesData] = await Promise.all([
          getUnits(),
          getCategories(1, ""),
        ]);
        setUnits(unitsData || []);
        const cats = Array.isArray(categoriesData)
          ? categoriesData
          : categoriesData?.results || [];
        setAllCategories(cats);
      } catch {
        toast.error("Failed to load data");
      }
    };
    fetchData();
  }, []);


  // ══════════════════════════════════════════
// INIT - Edit mode OR new BOM number
// ══════════════════════════════════════════
useEffect(() => {
  const initData = async () => {
    if (isEditMode) {
      try {
        setLoading(true);
        const bomData = await getBOMById(id);
        if (!bomData) return;

        const components = bomData.components || [];

        // ── Step 1: formData set karo ──
        setFormData({
          bom_no: bomData.bom_no || "",
          bom_date:
            bomData.bom_date || new Date().toISOString().split("T")[0],
          bom_name: bomData.bom_name || "",
          components: components.map((comp) => ({
            ...comp,
            category: comp.category || null,
            product_type: comp.product_type || null,
            item: comp.item || null,
            unit: comp.unit || null,
          })),
        });

        // ── Step 2: Options load karo ──
        const ptOptionsMap = {};
        const itemOptionsMap = {};

        await Promise.all(
          components.map(async (comp, index) => {
            const catId = comp.category?.id;
            const ptId = comp.product_type?.id;

            if (!catId) return;

            // ✅ Product Types load by Category
            try {
              const sizesData = await getSizes("", catId);
              ptOptionsMap[index] = Array.isArray(sizesData)
                ? sizesData
                : sizesData?.results || [];
            } catch {
              ptOptionsMap[index] = [];
            }

            // ✅ Items load by PRODUCT TYPE (size_id) — Category se NAHI
            if (ptId) {
              try {
                const items = await fetchItemsByCategory(catId, "", ptId);
                if (
                  comp.item &&
                  !items.find((i) => i.id === comp.item?.id)
                ) {
                  items.unshift(comp.item);
                }
                itemOptionsMap[index] = items;
              } catch {
                itemOptionsMap[index] = [];
              }
            } else {
              itemOptionsMap[index] = [];
            }
          }),
        );

        setComponentProductTypeOptions(ptOptionsMap);
        setComponentItemOptions(itemOptionsMap);
      } catch {
        toast.error("Failed to fetch BOM details");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const res = await getNextBomNo();
        setFormData((prev) => ({ ...prev, bom_no: res.bom_no }));
      } catch {
        toast.error("Failed to fetch BOM number");
      }
    }
  };
  initData();
}, [id, isEditMode]);

  // ══════════════════════════════════════════
  // DRAFT - Load from sessionStorage
  // ══════════════════════════════════════════
  useEffect(() => {
    if (!isEditMode) {
      const draft = sessionStorage.getItem("bomDraft");
      if (draft) {
        try {
          setFormData(JSON.parse(draft));
          toast.info("Loaded BOM draft");
        } catch {}
      }
    }
  }, [isEditMode]);

  // HELPER - Fetch items by category

  const fetchItemsByCategory = async (
    categoryId,
    search = "",
    sizeId = null,
  ) => {
    try {
      const response = await fetchItemsByCategoryAndSubcategory(
        categoryId,
        0,
        1,
        search,
        sizeId,
      );
      return response?.results || [];
    } catch {
      return [];
    }
  };

  const loadItemsForRow = useMemo(
    () =>
      debounce(async (index, sizeId, categoryId, search, callback) => {
        if (!sizeId) {
          callback([]);
          return;
        }
        const results = await fetchItemsByCategory(categoryId, search, sizeId);
        callback(results);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => loadItemsForRow.cancel();
  }, [loadItemsForRow]);

  // ══════════════════════════════════════════
  // HANDLERS
  // ══════════════════════════════════════════

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleComponentChange = (index, field, value) => {
    const components = [...formData.components];
    components[index] = { ...components[index], [field]: value };

    if (field === "product_type") {
      // Product type change — no reset needed, items already loaded by category
    }

    if (field === "item" && value) {
      components[index].description = value.description || "";
      components[index].unit = value.unit || null;
    }

    setFormData((prev) => ({ ...prev, components }));
  };

  // ── Category change → load product types + items ──
  const handleCategoryChange = async (index, newCategory) => {
    const components = [...formData.components];
    components[index] = {
      ...components[index],
      category: newCategory,
      product_type: null,
      item: null,
      description: "",
      unit: null,
    };
    setFormData((prev) => ({ ...prev, components }));

    if (newCategory?.id) {
      try {
        const sizesData = await getSizes("", newCategory.id);
        const pts = Array.isArray(sizesData)
          ? sizesData
          : sizesData?.results || [];
        setComponentProductTypeOptions((prev) => ({ ...prev, [index]: pts }));
        // ✅ Items empty rako — Product Type select hone par load honge
        setComponentItemOptions((prev) => ({ ...prev, [index]: [] }));
      } catch {
        setComponentProductTypeOptions((prev) => ({ ...prev, [index]: [] }));
        setComponentItemOptions((prev) => ({ ...prev, [index]: [] }));
      }
    } else {
      setComponentProductTypeOptions((prev) => ({ ...prev, [index]: [] }));
      setComponentItemOptions((prev) => ({ ...prev, [index]: [] }));
    }
  };

  // ── Product Type change ──
  const handleProductTypeChange = async (index, newProductType) => {
    const components = [...formData.components];
    components[index] = {
      ...components[index],
      product_type: newProductType,
      item: null,
      description: "",
      unit: null,
    };
    setFormData((prev) => ({ ...prev, components }));

    if (newProductType?.id) {
      try {
        const categoryId = components[index].category?.id || 0;
        const items = await fetchItemsByCategory(
          categoryId,
          "",
          newProductType.id,
        );
        setComponentItemOptions((prev) => ({ ...prev, [index]: items }));
      } catch {
        setComponentItemOptions((prev) => ({ ...prev, [index]: [] }));
      }
    } else {
      setComponentItemOptions((prev) => ({ ...prev, [index]: [] }));
    }
  };

  // ── Item search ──
  const handleComponentItemInputChange = (index, newInputValue) => {
    const comp = formData.components[index];
    if (comp.product_type?.id) {
      loadItemsForRow(
        index,
        comp.product_type.id,
        comp.category?.id,
        newInputValue,
        (results) => {
          setComponentItemOptions((prev) => ({ ...prev, [index]: results }));
        },
      );
    }
  };

  const handleComponentItemChange = (index, newValue) => {
    handleComponentChange(index, "item", newValue);
  };

  // ── Add component row ──
  const addComponent = () => {
    setFormData((prev) => ({
      ...prev,
      components: [...prev.components, emptyComponent()],
    }));
  };

  // ── Delete component row ──
  const deleteComponent = (index) => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index),
    }));

    // Reindex options
    const reindex = (prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (k < index) newState[k] = prev[k];
        else if (k > index) newState[k - 1] = prev[k];
      });
      return newState;
    };

    setComponentItemOptions(reindex);
    setComponentProductTypeOptions(reindex);
  };

  // ── Draft save ──
  const handleSaveDraft = () => {
    sessionStorage.setItem("bomDraft", JSON.stringify(formData));
    toast.info("BOM draft saved!");
  };

  // ── Unit helper ──
  const getSelectedUnit = (unitVal) => {
    if (!unitVal) return null;
    if (typeof unitVal === "object" && unitVal.id) {
      return units.find((u) => u.id === unitVal.id) || unitVal;
    }
    return units.find((u) => u.id === unitVal) || null;
  };

  // ══════════════════════════════════════════
  // SUBMIT
  // ══════════════════════════════════════════
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.bom_name?.trim()) {
      toast.error("BOM Name is required.");
      return;
    }
    if (!formData.components?.length) {
      toast.error("Please add at least one component.");
      return;
    }

    const invalid = formData.components.some(
      (c) => !c.category || !c.item || !c.quantity,
    );
    if (invalid) {
      toast.error("Each component must have Category, Item, and Quantity.");
      return;
    }

    setLoading(true);

    const payload = {
      bom_no: formData.bom_no,
      bom_date: formData.bom_date,
      bom_name: formData.bom_name,
      components: formData.components.map((c) => ({
        category_id: c.category?.id || c.category,
        product_type_id: c.product_type?.id || null, // ✅ ADD THIS
        item_id: c.item?.id || c.item,
        description: c.description,
        quantity: Number(c.quantity),
        unit_id: typeof c.unit === "object" ? c.unit?.id : c.unit,
      })),
    };

    try {
      if (isEditMode) {
        await updateBOM(id, payload);
        toast.success("BOM updated successfully!");
        sessionStorage.removeItem("bomDraft");
        setTimeout(() => navigate("/dashboard/BOMCreationList"), 1000);
      } else {
        await createBOM(payload);
        toast.success("BOM created successfully!");
        sessionStorage.removeItem("bomDraft");
        setTimeout(() => navigate("/dashboard/BOMCreationList"), 1000);
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to save BOM.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ══════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════
  return (
    <Box
      sx={{
        mx: { xs: 1, sm: 0 },
        px: { xs: 1, sm: 2 },
        mt: { xs: 4, sm: 0, md: 1 },
        pb: 4,
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      <ToastContainer theme={isDark ? "dark" : "light"} />

      <form onSubmit={handleSubmit} autoComplete="off">
        {/* ── HEADER ROW ── */}
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="BOM No."
              name="bom_no"
              value={formData.bom_no || ""}
              InputLabelProps={{ shrink: true }}
              disabled
              size="small"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              type="date"
              name="bom_date"
              label="BOM Date"
              value={formData.bom_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              required
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="BOM Name / Description"
              name="bom_name"
              value={formData.bom_name || ""}
              onChange={handleChange}
              placeholder="e.g., Hydraulic Lift Table BOM"
              InputLabelProps={{ shrink: true }}
              size="small"
              required
              sx={inputSx}
            />
          </Grid>

          {/* Desktop Buttons */}
          {!isMobile && (
            <Grid
              item
              sm={3}
              sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
            >
              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                sx={{
                  height: 30,
                  minWidth: 80,
                  borderRadius: 0,
                  fontSize: "0.65rem",
                  color: "#ff8400",
                  borderColor: "#ff8400",
                  "&:hover": {
                    borderColor: "#e07600",
                    backgroundColor: isDark
                      ? alpha("#ff8400", 0.12)
                      : "rgba(255,132,0,0.05)",
                  },
                }}
              >
                Draft
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{
                  height: 30,
                  minWidth: 100,
                  borderRadius: 0,
                  fontSize: "0.65rem",
                }}
              >
                {loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : isEditMode ? (
                  "Update BOM"
                ) : (
                  "Submit BOM"
                )}
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Mobile Buttons */}
        {isMobile && (
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleSaveDraft}
              sx={{
                height: 30,
                borderRadius: 0,
                fontSize: "0.65rem",
                color: "#ff8400",
                borderColor: "#ff8400",
                "&:hover": {
                  borderColor: "#e07600",
                  backgroundColor: isDark
                    ? alpha("#ff8400", 0.12)
                    : "rgba(255,132,0,0.05)",
                },
              }}
            >
              Draft
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ height: 30, borderRadius: 0, fontSize: "0.65rem" }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        )}

        {/* ══════════════════════════════════════════ */}
        {/* COMPONENTS                                */}
        {/* ══════════════════════════════════════════ */}
        <Box sx={{ mt: 1.5 }}>
          {isMobile ? (
            /* ── MOBILE CARDS ── */
            <Box>
              {formData.components.map((comp, index) => (
                <Card
                  key={index}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    backgroundImage: "none",
                    boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "none",
                  }}
                >
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    {/* Card Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "primary.main",
                        }}
                      >
                        Component #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => deleteComponent(index)}
                        sx={{
                          color: "error.main",
                          borderRadius: 1,
                          width: 28,
                          height: 28,
                          bgcolor: isDark
                            ? alpha(theme.palette.error.main, 0.15)
                            : "#ffebee",
                          "&:hover": {
                            bgcolor: isDark
                              ? alpha(theme.palette.error.main, 0.25)
                              : "#ffcdd2",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {/* ✅ Category FIRST */}
                      <Autocomplete
                        options={allCategories}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={comp.category || null}
                        onChange={(e, val) => handleCategoryChange(index, val)}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            required
                            sx={inputSx}
                          />
                        )}
                      />

                      {/* ✅ Product Type SECOND */}
                      <Autocomplete
                        options={componentProductTypeOptions[index] || []}
                        getOptionLabel={(opt) => opt.size || ""}
                        value={comp.product_type || null}
                        onChange={(e, val) =>
                          handleProductTypeChange(index, val)
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        disabled={!comp.category}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Product Type"
                            sx={inputSx}
                          />
                        )}
                      />

                      {/* Item */}
                      <Autocomplete
                        options={componentItemOptions[index] || []}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={comp.item || null}
                        onChange={(e, val) =>
                          handleComponentItemChange(index, val)
                        }
                        onInputChange={(e, newVal) =>
                          handleComponentItemInputChange(index, newVal)
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        disabled={!comp.product_type}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Item"
                            required
                            sx={inputSx}
                          />
                        )}
                      />

                      {/* Description */}
                      <TextField
                        fullWidth
                        label="Description"
                        value={comp.description}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={inputSx}
                      />

                      {/* Qty + Unit */}
                      <Grid container spacing={1}>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Quantity"
                            value={comp.quantity}
                            onChange={(e) =>
                              handleComponentChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            size="small"
                            required
                            inputProps={{ min: 0 }}
                            sx={inputSx}
                          />
                        </Grid>
                        <Grid item xs={7}>
                          <Autocomplete
                            options={units}
                            getOptionLabel={(opt) => opt.name || ""}
                            value={getSelectedUnit(comp.unit)}
                            onChange={(e, val) =>
                              handleComponentChange(index, "unit", val)
                            }
                            isOptionEqualToValue={(opt, val) =>
                              opt.id === val.id
                            }
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Unit"
                                sx={inputSx}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              <Button
                variant="outlined"
                onClick={addComponent}
                startIcon={<AddIcon />}
                fullWidth
                sx={{ borderRadius: 0, height: 30, fontSize: "0.65rem" }}
              >
                Add Component
              </Button>
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  mb: 1,
                  overflowX: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  bgcolor: "background.paper",
                  backgroundImage: "none",
                  mx: -2,
                  width: "calc(100% + 32px)",
                  boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: 1050,
                    "& td": { py: 0.9, px: 1 },
                    "& th": { py: 0.6, px: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          color: "text.primary",
                          borderBottom: "2px solid",
                          borderColor: "primary.main",
                          whiteSpace: "nowrap",
                          backgroundColor: tableHeadBg,
                          letterSpacing: "0.04em",
                        },
                      }}
                    >
                      <TableCell sx={{ width: 40 }}>S.No</TableCell>
                      <TableCell sx={{ width: 170 }}>Category</TableCell>
                      <TableCell sx={{ width: 160 }}>Product Type</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: 80 }}>Qty</TableCell>
                      <TableCell sx={{ width: 120 }}>Unit</TableCell>
                      <TableCell align="center" sx={{ width: 50 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {formData.components.map((comp, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: rowBg(index),
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            backgroundColor: isDark
                              ? alpha(theme.palette.primary.main, 0.1)
                              : "#f0f7ff",
                          },
                          "& td": {
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          },
                        }}
                      >
                        {/* S.No */}
                        <TableCell
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "text.secondary",
                          }}
                        >
                          {index + 1}
                        </TableCell>

                        {/* ✅ Category FIRST */}
                        <TableCell sx={{ minWidth: 170 }}>
                          <Autocomplete
                            options={allCategories}
                            getOptionLabel={(opt) => opt.name || ""}
                            value={comp.category || null}
                            onChange={(e, val) =>
                              handleCategoryChange(index, val)
                            }
                            isOptionEqualToValue={(opt, val) =>
                              opt.id === val.id
                            }
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                required
                                sx={inputSx}
                              />
                            )}
                          />
                        </TableCell>

                        {/* ✅ Product Type SECOND */}
                        <TableCell sx={{ minWidth: 160 }}>
                          <Autocomplete
                            options={componentProductTypeOptions[index] || []}
                            getOptionLabel={(opt) => opt.size || ""}
                            value={comp.product_type || null}
                            onChange={(e, val) =>
                              handleProductTypeChange(index, val)
                            }
                            isOptionEqualToValue={(opt, val) =>
                              opt.id === val.id
                            }
                            disabled={!comp.category}
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Product Type"
                                sx={inputSx}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Item */}
                        <TableCell sx={{ minWidth: 170 }}>
                          <Autocomplete
                            options={componentItemOptions[index] || []}
                            getOptionLabel={(opt) => opt.name || ""}
                            value={comp.item || null}
                            onChange={(e, val) =>
                              handleComponentItemChange(index, val)
                            }
                            onInputChange={(e, newVal) =>
                              handleComponentItemInputChange(index, newVal)
                            }
                            isOptionEqualToValue={(opt, val) =>
                              opt.id === val.id
                            }
                            disabled={!comp.product_type}
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item"
                                required
                                sx={inputSx}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Description */}
                        <TableCell sx={{ minWidth: 150 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={comp.description}
                            onChange={(e) =>
                              handleComponentChange(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            sx={inputSx}
                          />
                        </TableCell>

                        {/* Quantity */}
                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            value={comp.quantity}
                            onChange={(e) =>
                              handleComponentChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            required
                            inputProps={{ min: 0 }}
                            sx={inputSx}
                          />
                        </TableCell>

                        {/* Unit */}
                        <TableCell>
                          <Autocomplete
                            options={units}
                            getOptionLabel={(opt) => opt.name || ""}
                            value={getSelectedUnit(comp.unit)}
                            onChange={(e, val) =>
                              handleComponentChange(index, "unit", val)
                            }
                            isOptionEqualToValue={(opt, val) =>
                              opt.id === val.id
                            }
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Unit"
                                sx={inputSx}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Delete */}
                        <TableCell align="center">
                          <IconButton
                            onClick={() => deleteComponent(index)}
                            size="small"
                            sx={{
                              color: "error.main",
                              p: 0.3,
                              "&:hover": {
                                bgcolor: isDark
                                  ? alpha(theme.palette.error.main, 0.15)
                                  : "#ffebee",
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                variant="outlined"
                onClick={addComponent}
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 0,
                  height: 30,
                  fontSize: "0.65rem",
                  mt: 0.5,
                }}
              >
                Add Component
              </Button>
            </>
          )}
        </Box>
      </form>
    </Box>
  );
}
